import { useMemo } from "react";
import { useReportingQuery } from "web_myplann/Api/Finances/useReportingQuery";
import { PortfolioReportingOverviewTypeEnum } from "web_myplann/Infrastructure/Api/Api";
import { Colors } from "web_myplann/Infrastructure/Themes/Colors";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import type { DashboardType } from "../Components/DashboardTitleCard";

type DashboardConfig = {
	type: PortfolioReportingOverviewTypeEnum;
	gradientFrom: string;
	gradientTo: string;
};

const dashboardConfigs: DashboardConfig[] = [
	{
		type: PortfolioReportingOverviewTypeEnum.Total,
		gradientFrom: Colors.DashboardGradientTotalFrom,
		gradientTo: Colors.DashboardGradientTotalTo,
	},
	{
		type: PortfolioReportingOverviewTypeEnum.Investment,
		gradientFrom: Colors.DashboardGradientInvestmentFrom,
		gradientTo: Colors.DashboardGradientInvestmentTo,
	},
	{
		type: PortfolioReportingOverviewTypeEnum.InvestmentProperty,
		gradientFrom: Colors.DashboardGradientInvestmentPropertyFrom,
		gradientTo: Colors.DashboardGradientInvestmentPropertyTo,
	},
	{
		type: PortfolioReportingOverviewTypeEnum.Cash,
		gradientFrom: Colors.DashboardGradientCashFrom,
		gradientTo: Colors.DashboardGradientCashTo,
	},
	{
		type: PortfolioReportingOverviewTypeEnum.Other,
		gradientFrom: Colors.DashboardGradientOtherFrom,
		gradientTo: Colors.DashboardGradientOtherTo,
	},
];

const OverviewTypeResources = Resources.Dashboard.OverviewType;

export const useDashboardData = () => {
	const { t } = useResource();
	const { data: reportingData, isLoading, isFetching } = useReportingQuery();

	const dashboards = useMemo<DashboardType[]>(() => {
		const items = reportingData?.data;

		return dashboardConfigs.flatMap((config, index) => {
			const apiItem = items?.find((item) => item.type === config.type);
			if (!apiItem) return [];

			const itemCount = apiItem.itemCount ?? 0;
			const itemCountWithoutValue = apiItem.itemCountWithoutValue ?? 0;
			const value = apiItem.value ?? 0;
			const nettoValue = apiItem.nettoValue;
			const hasNetValue = nettoValue != null && nettoValue !== value;

			if (value === 0 && itemCount === 0 && !hasNetValue) {
				return [];
			}

			return [
				{
					id: index,
					overviewType: config.type,
					type: t(OverviewTypeResources[config.type]),
					value: apiItem.value ?? null,
					itemCountWithoutValue,
					netValue: hasNetValue ? nettoValue : undefined,
					currency: apiItem.currency ?? "",
					evaluation: apiItem.evaluation ?? undefined,
					gradientFrom: config.gradientFrom,
					gradientTo: config.gradientTo,
				},
			];
		});
	}, [reportingData, t]);

	const isEmpty = dashboards.length === 0;

	return { dashboards, isLoading, isFetching, isEmpty };
};

export const getDashboardIndexByType = (
	dashboards: DashboardType[],
	type: PortfolioReportingOverviewTypeEnum,
) => dashboards.findIndex((d) => d.overviewType === type);
